import React, { createContext, useContext, useRef, type ReactNode, type RefObject } from 'react';
import { Platform, StyleSheet, View } from 'react-native';
import { BlurTargetView, BlurView, type BlurViewProps } from 'expo-blur';

type BlurTargetRef = RefObject<View | null>;

const AppBlurRootContext = createContext<BlurTargetRef | null>(null);

/**
 * Android: wraps the app in the `BlurTargetView` that modal blurs sample.
 * Elsewhere it renders `children` unchanged.
 */
export function AppBlurRoot({ children }: { children: ReactNode }) {
  const targetRef = useRef<View>(null);

  if (Platform.OS !== 'android') return <>{children}</>;

  return (
    <AppBlurRootContext.Provider value={targetRef}>
      <BlurTargetView ref={targetRef} collapsable={false} style={styles.fill}>
        {children}
      </BlurTargetView>
    </AppBlurRootContext.Provider>
  );
}

export type AppBlurViewProps = BlurViewProps & {
  /**
   * Blur the whole app behind this view on Android. Only for views in their own
   * native window (RN `Modal`, `react-native-modal` with `coverScreen`): a blur
   * inside its own target would sample itself.
   */
  blurApp?: boolean;
};

/** `expo-blur` BlurView that actually blurs on Android (falls back to a tint without a target). */
export function AppBlurView({ blurApp, blurTarget, blurMethod, ...props }: AppBlurViewProps) {
  const rootTarget = useContext(AppBlurRootContext);
  const target = blurTarget ?? (blurApp ? rootTarget : null);

  if (Platform.OS !== 'android' || !target) {
    return <BlurView blurTarget={blurTarget} blurMethod={blurMethod} {...props} />;
  }

  return (
    <BlurView
      {...props}
      blurTarget={target}
      blurMethod={blurMethod ?? 'dimezisBlurViewSdk31Plus'}
    />
  );
}

const styles = StyleSheet.create({
  fill: { flex: 1 },
});
